import axios from 'axios';

// Ruta a la que se redirige cuando la sesión murió
const LOGIN_PATH = '/iniciar-sesion';
// Ruta para cuando hay sesión válida pero no permiso sobre el recurso.
const DENIED_PATH = '/acceso-denegado';

// Los tokens ahora viven en cookies HttpOnly (no en localStorage), por lo que
// TODAS las peticiones deben enviar cookies con withCredentials.
const axiosInstance = axios.create({ withCredentials: true });

// Lee una cookie NO-HttpOnly por nombre (las cookies csrf_* sí son legibles).
const getCookie = (name) => {
  const match = document.cookie.match(
    new RegExp('(?:^|;\\s*)' + name + '=([^;]*)')
  );
  return match ? decodeURIComponent(match[1]) : null;
};

// Cierra la sesión: limpia el estado local y manda al login.
const logout = () => {
  if (window.location.pathname !== LOGIN_PATH) {
    localStorage.clear();
    window.location.href = LOGIN_PATH;
  }
};

// Sesión válida pero sin permiso sobre el recurso: mandar a la pantalla de
// acceso denegado en vez de dejar la vista en blanco. No limpia la sesión.
const denied = () => {
  if (window.location.pathname !== DENIED_PATH) {
    window.location.href = DENIED_PATH;
  }
};

// Control de refresh concurrente: si varias peticiones fallan con 401 al mismo
// tiempo, solo se dispara UNA llamada a /api/refresh y el resto la reutiliza.
let isRefreshing = false;
let refreshPromise = null;

const refreshAccessToken = () => {
  // El refresh token viaja solo en su cookie HttpOnly; aquí únicamente hay que
  // acompañar el CSRF double-submit leyendo la cookie csrf_refresh_token.
  const csrfRefresh = getCookie('csrf_refresh_token');
  return axios.post(
    `${process.env.REACT_APP_URL}/api/refresh`,
    {},
    {
      withCredentials: true,
      headers: csrfRefresh ? { 'X-CSRF-TOKEN': csrfRefresh } : {},
    }
  );
};

// Interceptor de request: adjunta el CSRF del access token (double-submit).
axiosInstance.interceptors.request.use(
  (config) => {
    const csrfAccess = getCookie('csrf_access_token');
    if (csrfAccess) {
      config.headers['X-CSRF-TOKEN'] = csrfAccess;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Interceptor de response: si el access token expiró (401), se renueva con el
// refresh token UNA vez y se reintenta la petición original. Si el refresh
// también falla (o el token es inválido -> 422), se cierra sesión.
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const originalRequest = error?.config;

    // Token inválido/malformado: no tiene sentido refrescar.
    if (status === 422) {
      logout();
      return Promise.reject(error);
    }

    // Sesión válida pero sin permiso sobre el recurso (IDOR / rol ajeno):
    // la API responde 302 con detail "acceso no permitido". Otros 302 de
    // negocio (saldo, organización inactiva) los maneja cada vista.
    if (status === 302 && error?.response?.data?.detail === 'acceso no permitido') {
      denied();
      return Promise.reject(error);
    }

    // Access token expirado: intentar refresh una sola vez por petición.
    if (status === 401 && originalRequest && !originalRequest._retry) {
      originalRequest._retry = true;

      if (!isRefreshing) {
        isRefreshing = true;
        refreshPromise = refreshAccessToken().finally(() => {
          isRefreshing = false;
        });
      }

      return refreshPromise
        .then(() => {
          // El nuevo access token ya quedó en la cookie; reintentar con el
          // CSRF actualizado.
          const csrfAccess = getCookie('csrf_access_token');
          if (csrfAccess) {
            originalRequest.headers['X-CSRF-TOKEN'] = csrfAccess;
          }
          return axiosInstance(originalRequest);
        })
        .catch((refreshError) => {
          // El refresh falló (expiró la semana o es inválido): cerrar sesión.
          logout();
          return Promise.reject(refreshError);
        });
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
